/**
 * Scroll-based Color Variable Updater
 * Changes CSS custom property based on scroll position through .blocks element
 * Color progression: #D7DA80 -> #ECDF4F -> #FFBB32 -> #FF6F3B
 */

class ScrollColorUpdater {
    constructor() {
        this.colors = [
            { hex: '#D7DA80', rgb: [215, 218, 128] }, // 0%   - olive
            { hex: '#ECDF4F', rgb: [236, 223, 79] },  // 25%  - yellow
            { hex: '#FFBB32', rgb: [255, 187, 50] },  // 50%  - orange
            { hex: '#FF6F3B', rgb: [255, 111, 59] }   // 100% - red
        ];
        
        this.blocksElement = null;
        this.isScrolling = false;
        
        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }
    
    setup() {
        this.blocksElement = document.querySelector('.blocks');
        
        if (!this.blocksElement) {
            console.warn('ScrollColorUpdater: .blocks element not found');
            return;
        }
        
        // Set initial color
        this.updateColorVariable(0);
        
        // Add scroll listener with throttling
        this.addScrollListener();
        
        // Handle resize events
        window.addEventListener('resize', () => this.throttle(() => this.handleScroll(), 16));
    }
    
    addScrollListener() {
        let ticking = false;
        
        window.addEventListener('scroll', () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    this.handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });
    }
    
    handleScroll() {
        if (!this.blocksElement) return;
        
        const scrollPercentage = this.calculateScrollPercentage();
        this.updateColorVariable(scrollPercentage);
    }
    
    calculateScrollPercentage() {
        const rect = this.blocksElement.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const elementHeight = rect.height;
        const viewportHeight = window.innerHeight;
        const elementBottom = elementTop + elementHeight;
        
        // 0% = when top of element enters viewport (element top = viewport bottom)
        // 100% = when bottom of element is visible (element bottom = viewport bottom)
        
        const viewportBottom = window.scrollY + viewportHeight;
        
        // Before element enters viewport
        if (viewportBottom < elementTop) return 0;
        
        // After bottom of element is fully visible
        if (viewportBottom >= elementBottom) return 1;
        
        // Calculate progress from 0% (top enters) to 100% (bottom visible)
        const totalDistance = elementBottom - elementTop;
        const currentProgress = viewportBottom - elementTop;
        
        return Math.max(0, Math.min(1, currentProgress / totalDistance));
    }
    
    updateColorVariable(percentage) {
        const color = this.interpolateColor(percentage);
        document.documentElement.style.setProperty('--scroll-color', color);
    
    }
    
    interpolateColor(percentage) {
        // Clamp percentage between 0 and 1
        percentage = Math.max(0, Math.min(1, percentage));
        
        // Define the segments (0-25%, 25-50%, 50-100%)
        let segment;
        let localProgress;
        
        if (percentage <= 0.25) {
            segment = 0; // D7DA80 -> ECDF4F
            localProgress = percentage / 0.25;
        } else if (percentage <= 0.5) {
            segment = 1; // ECDF4F -> FFBB32  
            localProgress = (percentage - 0.25) / 0.25;
        } else {
            segment = 2; // FFBB32 -> FF6F3B
            localProgress = (percentage - 0.5) / 0.5;
        }
        
        const startColor = this.colors[segment];
        const endColor = this.colors[segment + 1];
        
        const r = Math.round(startColor.rgb[0] + (endColor.rgb[0] - startColor.rgb[0]) * localProgress);
        const g = Math.round(startColor.rgb[1] + (endColor.rgb[1] - startColor.rgb[1]) * localProgress);
        const b = Math.round(startColor.rgb[2] + (endColor.rgb[2] - startColor.rgb[2]) * localProgress);
        
        return `rgb(${r}, ${g}, ${b})`;
    }
    
    // Utility function for throttling
    throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
}

// Initialize when module is imported
export { ScrollColorUpdater };

// Also initialize immediately if not using modules
if (typeof module === 'undefined') {
    new ScrollColorUpdater();
}